import SignatureCanvas from 'react-signature-canvas'
import { IonIcon, IonLabel } from '@ionic/react'
import React, { useState, useEffect } from 'react'
import { getStyleForPHI, IPHIComponent } from '../../../components/phi/phi.component'
import './SignatureCanvas.css'
import { closeCircle, expandOutline, contractOutline } from 'ionicons/icons'
import { Link, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface SignatureCanvasPadProps extends IPHIComponent {
  canvasRef?: any
  isCanvasVisible: boolean
  canvasContent?: ICanvasContent
  onEnd?: (event: MouseEvent) => void
  onClear?: () => void
  isCanvasExpandable?: boolean
  canvasHeight?: number
  canvasWidth?: number
  onCanvasExpandChange?: (expanded: boolean) => void
  minWidth?: number
  maxWidth?: number
  penColor?: string
}

interface ICanvasContent {
  canvasData?: string
  canvasHeader?: string
  canvasLink?: string
  canvasLabel?: string
  icon?: string
  isCanvasLinkVisible?: boolean
  isSectionLocked?: boolean
}

const SignatureCanvasPad: React.FC<SignatureCanvasPadProps> = (props: SignatureCanvasPadProps) => {
  const {
    isPHI,
    canvasRef,
    isCanvasVisible = true,
    canvasContent,
    onEnd,
    onClear,
    isCanvasExpandable = false,
    canvasHeight,
    canvasWidth,
    onCanvasExpandChange,
    minWidth = 1.5,
    maxWidth = 1.5,
    penColor = '#000',
  } = props

  const { t } = useTranslation()
  const history = useHistory()
  const [isExpanded, setIsExpanded] = useState(false)

  const clear = () => {
    if (canvasRef.current) {
      canvasRef.current.clear()
      canvasRef.current.on()
      if (onClear) onClear()
    }
  }

  useEffect(() => {
    if (isExpanded) {
      const newExpandedState = !isExpanded
      setIsExpanded(newExpandedState)
      onCanvasExpandChange?.(newExpandedState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCanvasVisible])

  const toggleExpand = () => {
    const newExpandedState = isExpanded ? false : true
    setIsExpanded(newExpandedState)
    onCanvasExpandChange?.(newExpandedState)
  }

  useEffect(() => {
    if (canvasRef.current && isCanvasExpandable) {
      const canvas = canvasRef.current.getCanvas()
      if (canvas) {
        if (isExpanded) {
          const wrapperElement = document.querySelector('.signature-canvas-wrapper.expanded')
          if (wrapperElement) {
            const rect = wrapperElement.getBoundingClientRect()
            const wrapperWidth = rect.width - 20
            const wrapperHeight = rect.height - 20
            canvas.width = wrapperWidth
            canvas.height = wrapperHeight
          }
        } else {
          canvas.width = canvasWidth || 300
          canvas.height = canvasHeight || 200
        }
      }
    }
  }, [isExpanded, canvasRef, isCanvasExpandable, canvasWidth, canvasHeight])

  return (
    <>
      <div
        data-private={isPHI}
        style={getStyleForPHI(isPHI)}
        className={`signature-canvas-wrapper ${isExpanded ? 'expanded' : ''}`}>
        {canvasContent?.canvasData && (
          <IonIcon className='sign-cross-icon' icon={closeCircle} color='medium' onClick={clear} />
        )}
        {canvasContent?.canvasHeader && (
          <IonLabel className='section-type-label' color='medium'>
            {canvasContent?.canvasHeader}
          </IonLabel>
        )}

        {canvasContent?.canvasLink && (
          <div
            onClick={() => {
              canvasContent?.canvasLink && history?.push(canvasContent?.canvasLink)
            }}
            className='custom-signature-section'>
            <div className='custom-lock-section'>
              {canvasContent?.icon && <IonIcon color='medium' className='lock-icon' icon={canvasContent?.icon} />}
              {canvasContent?.canvasLink && !canvasContent?.canvasLabel && (
                <IonLabel color='medium' className='signture-label'>
                  {t('signature.canvasPad.signatoryMustAgree')}
                  <Link id='canvas-link' className='ion-color-zoll-blue' to={canvasContent?.canvasLink}>
                    {t('signature.canvasPad.terms')}
                  </Link>
                  {t('signature.canvasPad.priorToSigning')}
                </IonLabel>
              )}
              {canvasContent?.canvasLabel && (
                <IonLabel className='signture-label' color='medium'>
                  {canvasContent?.canvasLabel}
                </IonLabel>
              )}
            </div>
          </div>
        )}

        {canvasContent?.canvasLabel && !canvasContent?.canvasLink && (
          <div className='custom-signature-section'>
            <div className='custom-lock-section'>
              {canvasContent?.icon && <IonIcon color='medium' className='lock-icon' icon={canvasContent?.icon} />}
              <IonLabel className='signture-label' color='medium'>
                {canvasContent?.canvasLabel}
              </IonLabel>
            </div>
          </div>
        )}

        {isCanvasVisible && (
          <div className='canvas-container'>
            <SignatureCanvas
              ref={canvasRef}
              canvasProps={{
                width: canvasWidth,
                height: canvasHeight,
                className: 'signature-canvas',
              }}
              clearOnResize={false}
              onEnd={onEnd}
              minWidth={minWidth}
              maxWidth={maxWidth}
              penColor={penColor}
            />
            {isCanvasExpandable && (
              <IonIcon
                className='expand-icon'
                icon={isExpanded ? contractOutline : expandOutline}
                color='medium'
                onClick={toggleExpand}
              />
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default SignatureCanvasPad
